import { useCallback, useState } from 'react'

export type StepConfig = {
  id: string
  label: string
}

export type FormStepsState = {
  formStep: number
  user: boolean
  expandedStep: string | null
}

export type FormStepsActions = {
  goToStep: (step: number, userInitiated?: boolean) => void
  toggleExpand: (stepId: string) => void
  closeAllExpands: () => void
  setUser: (value: boolean) => void
}

export type UseFormStepsReturn = FormStepsState & FormStepsActions

/**
 * Custom hook for managing multi-step form navigation and state
 * @param initialStep - The initial step number (default: 1)
 * @returns Object containing step state and navigation functions
 */
export const useFormSteps = (initialStep: number = 1): UseFormStepsReturn => {
  const [formStep, setFormStep] = useState(initialStep)
  const [user, setUser] = useState(false)
  const [expandedStep, setExpandedStep] = useState<string | null>(null)

  const goToStep = useCallback(
    (newStep: number, userInitiated: boolean = true) => {
      setUser(userInitiated)
      setFormStep(newStep)
      setExpandedStep(null)
    },
    [],
  )

  const toggleExpand = useCallback((stepId: string) => {
    setExpandedStep((current) => (current === stepId ? null : stepId))
  }, [])

  const closeAllExpands = useCallback(() => {
    setExpandedStep(null)
  }, [])

  return {
    formStep,
    user,
    expandedStep,
    goToStep,
    toggleExpand,
    closeAllExpands,
    setUser,
  }
}

export default useFormSteps
